import React from "react";

export default function useHorizontalOverflow(scrollRef) {
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [hasMoreToRight, setHasMoreToRight] = React.useState(false);

  const update = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Small tolerance prevents fractional-pixel rounding from flickering the cue.
    const tolerance = 2;
    const nextHasOverflow = el.scrollWidth > el.clientWidth + tolerance;
    const nextHasMoreToRight =
      nextHasOverflow &&
      el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance;

    setHasOverflow(nextHasOverflow);
    setHasMoreToRight(nextHasMoreToRight);
  }, [scrollRef]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    let frameId = null;
    const scheduleUpdate = () => {
      if (frameId != null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(update);
    };

    scheduleUpdate();
    el.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null;
    resizeObserver?.observe(el);

    // OSMD/SVG widgets often establish their true width after React renders.
    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(scheduleUpdate)
        : null;
    mutationObserver?.observe(el, {
      subtree: true,
      childList: true,
      attributes: true
    });

    return () => {
      if (frameId != null) cancelAnimationFrame(frameId);
      el.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [scrollRef, update]);

  return { hasOverflow, hasMoreToRight };
}
